import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { fadeInUp } from '../utils/animations';
import { auth } from '../services/api';
import '../styles/VerifyEmail.css';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState('loading'); // loading, success, error, already_verified
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [resending, setResending] = useState(false);

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setStatus('error');
      setMessage('Érvénytelen link. Kérlek ellenőrizd az emailedet.');
      return;
    }

    verifyEmailToken(token);
  }, [searchParams]);

  const verifyEmailToken = async (token) => {
    try {
      const response = await auth.verifyEmail(token);

      if (response.data.alreadyVerified) {
        setStatus('already_verified');
        setMessage('Ez az email már megerősítésre került. Most már bejelentkezhetsz.');
      } else {
        setStatus('success');
        setMessage(response.data.message || 'Email sikeresen megerősítve!');
      }

      // Redirect login-hoz 3 másodperc után
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      setStatus('error');
      const errorMsg = error.response?.data?.message || 'Valami hiba történt. Kérlek próbáld újra.';
      setMessage(errorMsg);

      // Ha token expired, kérdezzük meg az emailt
      if (error.response?.data?.code === 'TOKEN_EXPIRED') {
        // Email kérése
      }
    }
  };

  const handleResendEmail = async () => {
    if (!email) {
      alert('Kérlek add meg az email címedet');
      return;
    }

    setResending(true);
    try {
      const response = await auth.resendVerification(email);
      setMessage(response.data.message || 'Email újraküldve!');
      setStatus('success');
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Email küldése sikertelen';
      setMessage(errorMsg);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="verify-email">
      <motion.div
        className="verify-email-container"
        initial="hidden"
        animate="visible"
        variants={fadeInUp}
      >
        <div className="verify-email-card card">
          {/* Loading State */}
          {status === 'loading' && (
            <>
              <div className="verify-icon loading-icon">
                <div className="spinner"></div>
              </div>
              <h1>Email Megerősítése...</h1>
              <p>Kérlek várj, amíg megerősítjük az email címedet.</p>
            </>
          )}

          {/* Success State */}
          {status === 'success' && (
            <>
              <div className="verify-icon success-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
              </div>
              <h1>Sikeres Megerősítés! ✨</h1>
              <p className="success-message">{message}</p>
              <p className="redirect-message">Átirányítás a bejelentkezéshez 3 másodperc múlva...</p>
              <button
                className="btn btn-primary"
                onClick={() => navigate('/login')}
              >
                Bejelentkezés Most
              </button>
            </>
          )}

          {/* Already Verified State */}
          {status === 'already_verified' && (
            <>
              <div className="verify-icon info-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
              </div>
              <h1>Már Megerősítve</h1>
              <p className="info-message">{message}</p>
              <button
                className="btn btn-primary"
                onClick={() => navigate('/login')}
              >
                Bejelentkezés
              </button>
            </>
          )}

          {/* Error State */}
          {status === 'error' && (
            <>
              <div className="verify-icon error-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
              </div>
              <h1>Hiba Történt</h1>
              <p className="error-message">{message}</p>

              <div className="resend-section">
                <p>Szeretnéd újraküldeni a megerősítő emailt?</p>
                <div className="resend-form">
                  <input
                    type="email"
                    placeholder="Email címed"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="form-input"
                  />
                  <button
                    className="btn btn-primary"
                    onClick={handleResendEmail}
                    disabled={resending}
                  >
                    {resending ? 'Küldés...' : 'Email Újraküldése'}
                  </button>
                </div>
              </div>

              <button
                className="btn btn-outline"
                onClick={() => navigate('/')}
              >
                Vissza a Főoldalra
              </button>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default VerifyEmail;
